import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useToaster } from '../../../utils/hooks';
import { ToastStyle } from '../../../utils/hooks/useToaster';
import { IClubMemberDto, IPagedResult } from '../../../utils/constants/interfaces';

interface SearchParams {
  lastName: string;
  firstName: string;
  birthDate: string;
}

const emptySearchResults: IPagedResult<IClubMemberDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 30,
  pageCount: 0,
  resultCount: 0
};

const useClubMemberAddSearch = () => {
  const api = useApi();
  const navigate = useNavigate();
  const toaster = useToaster();

  const [searchParams, setSearchParams] = useState<SearchParams>({
    lastName: '',
    firstName: '',
    birthDate: ''
  });

  const [searchResults, setSearchResults] =
    useState<IPagedResult<IClubMemberDto[]>>(emptySearchResults);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const updateSearchParam = (key: keyof SearchParams, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value
    }));
    // Reset search state when parameters change
    if (hasSearched) {
      setHasSearched(false);
      setSearchResults(emptySearchResults);
    }
  };

  const performSearch = useCallback(async () => {
    const validateSearch = (): boolean => {
      // Require all three fields: firstName, lastName, and birthDate
      if (!searchParams.lastName) {
        toaster.addToast('Last name is required for searching', ToastStyle.ERROR, 3000);
        return false;
      }

      if (!searchParams.firstName) {
        toaster.addToast('First name is required for searching', ToastStyle.ERROR, 3000);
        return false;
      }

      if (!searchParams.birthDate) {
        toaster.addToast('Birth date is required for searching', ToastStyle.ERROR, 3000);
        return false;
      }

      return true;
    };

    if (!validateSearch()) return;

    setIsSearching(true);

    try {
      // Build request body to match C# ClubMemberSearchRequest object (camelCase for JSON)
      const requestBody = {
        quickSearch: '',
        clubMemberId: null,
        firstName: searchParams.firstName,
        lastName: searchParams.lastName,
        emailAddress: '',
        birthDate: searchParams.birthDate ? new Date(searchParams.birthDate) : null,
        joinedDateFrom: null,
        joinedDateThru: null,
        includeSecondaryAdults: true, // Include secondary members in search
        firstJoinedFrom: null,
        firstJoinedThru: null,
        profession: '',
        nraMembersOnly: false,
        status: [], // Empty array = return all statuses
        membershipTypes: []
      };

      // Call API with search parameters
      const endpoint = `/members/search`;
      const response = await api.post<IClubMemberDto[]>(endpoint, JSON.stringify(requestBody));

      if (response?.status?.statusCode === 200 && response.response) {
        // Convert the response to the expected paged result format
        const pagedResult: IPagedResult<IClubMemberDto[]> = {
          currentPage: 1,
          results: response.response,
          pageSize: 30,
          pageCount: 1,
          resultCount: response.response.length
        };
        setSearchResults(pagedResult);
      } else {
        setSearchResults(emptySearchResults);
        toaster.addToast('Error performing search', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      setSearchResults(emptySearchResults);
      toaster.addToast('Error performing search', ToastStyle.ERROR, 5000);
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  }, [api, searchParams.birthDate, searchParams.firstName, searchParams.lastName, toaster]);

  const addNewMember = () => {
    // Navigate to the new member page with prefilled data
    void navigate('/members/new', {
      state: {
        prefill: {
          lastName: searchParams.lastName,
          firstName: searchParams.firstName,
          dateOfBirth: searchParams.birthDate
        }
      }
    });
  };

  const selectExistingMember = (memberId: string) => {
    void navigate(`/members/${memberId}`);
  };

  return {
    searchParams,
    updateSearchParam,
    performSearch,
    hasSearched,
    isSearching,
    searchResults,
    addNewMember,
    selectExistingMember,
    ToastContainer: toaster.ToastContainer
  };
};

export default useClubMemberAddSearch;
